import React, { useState, useCallback, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { RuleType, TimeCondition } from '../../../../domain/doctorShiftRule';
import DoctorMultiSelect from './DoctorMultiSelect';
import TimeConditionForm from './TimeConditionForm';

interface DoctorShiftRuleAddModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: {
    doctorIds: string[];
    ruleType: RuleType;
    timeConditions: TimeCondition[];
  }) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

const DEFAULT_CONDITIONS: TimeCondition[] = [
  {
    weekNumber: 'all',
    dayOfWeek: 'monday',
    startTime: '09:00',
    endTime: '17:00'
  }
];

/**
 * 医師シフトルール追加モーダル
 *
 * - 医師選択（複数可）
 * - ルールタイプ選択
 * - 時間条件設定
 */
const DoctorShiftRuleAddModal: React.FC<DoctorShiftRuleAddModalProps> = memo(({
  open,
  onClose,
  onSubmit,
  isLoading,
  error
}) => {
  const [selectedDoctorIds, setSelectedDoctorIds] = useState<string[]>([]);
  const [ruleType, setRuleType] = useState<RuleType>('assignable');
  const [timeConditions, setTimeConditions] = useState<TimeCondition[]>(DEFAULT_CONDITIONS);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setSelectedDoctorIds([]);
      setRuleType('assignable');
      setTimeConditions(DEFAULT_CONDITIONS);
      onClose();
    }
  }, [isLoading, onClose]);

  const handleSubmit = useCallback(async () => {
    const success = await onSubmit({
      doctorIds: selectedDoctorIds,
      ruleType,
      timeConditions
    });

    if (success) {
      setSelectedDoctorIds([]);
      setRuleType('assignable');
      setTimeConditions(DEFAULT_CONDITIONS);
    }
  }, [selectedDoctorIds, ruleType, timeConditions, onSubmit]);

  const isValid = selectedDoctorIds.length > 0 && timeConditions.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon color="primary" />
          <Typography variant="h6">
            シフトルールを追加
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 医師選択 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
            医師を選択
          </Typography>
          <DoctorMultiSelect
            selectedDoctorIds={selectedDoctorIds}
            onChange={setSelectedDoctorIds}
            disabled={isLoading}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* 時間条件 */}
        <Box>
          <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
            ルール設定
          </Typography>
          <TimeConditionForm
            conditions={timeConditions}
            ruleType={ruleType}
            onChange={setTimeConditions}
            onRuleTypeChange={setRuleType}
            disabled={isLoading}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {isLoading ? '追加中...' : '追加'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

DoctorShiftRuleAddModal.displayName = 'DoctorShiftRuleAddModal';

export default DoctorShiftRuleAddModal;
